(function (root, factory) {
  const rules = factory();
  if (typeof module === 'object' && module.exports) module.exports = rules;
  if (root) root.BBC_PARENT_PLAYOFFS_RULES = rules;
})(typeof window !== 'undefined' ? window : globalThis, function () {
  'use strict';

  const QUESTION_SECONDS = 15;
  const ROUND_LENGTH = 10;
  const OWNER_APPROVED_BATCH = 'parent-playoffs-expanded-2026-10-29-791';

  function shownSeconds(remainingMs) {
    if (!Number.isFinite(remainingMs)) return 0;
    return Math.max(0, Math.min(QUESTION_SECONDS, Math.ceil(remainingMs / 1000)));
  }

  function pointsForAnswer(correct, remainingMs) {
    return correct ? shownSeconds(remainingMs) : 0;
  }

  function shuffle(input, random = Math.random) {
    const items = [...input];
    for (let index = items.length - 1; index > 0; index -= 1) {
      const target = Math.floor(random() * (index + 1));
      [items[index], items[target]] = [items[target], items[index]];
    }
    return items;
  }

  function hasReleaseApproval(question, sources) {
    if (question?.reviewStatus !== 'approved') return false;
    const approvals = question.reviewApprovals;
    const clinical = approvals?.clinical;
    const editorial = approvals?.editorial;
    const validDate = (value) => {
      if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
      const timestamp = Date.parse(`${value}T00:00:00Z`);
      return Number.isFinite(timestamp) && new Date(timestamp).toISOString().slice(0, 10) === value;
    };
    const source = sources?.[question.source];
    const sourceVersionOrDate = source?.version || source?.checked;
    const owner = approvals?.ownerAttestation;
    if (owner?.decision === 'approved' && owner.batchId === OWNER_APPROVED_BATCH &&
        owner.questionId === question.id && validDate(owner.attestedAt) &&
        owner.sourceUrl === source?.url && sourceVersionOrDate &&
        owner.sourceVersionOrDate === sourceVersionOrDate) return true;
    return Boolean(
      clinical?.decision === 'approved' && clinical.reviewerId && clinical.qualification &&
      validDate(clinical.reviewedAt) && clinical.sourceUrl && clinical.sourceUrl === source?.url &&
      sourceVersionOrDate && clinical.sourceVersionOrDate === sourceVersionOrDate &&
      editorial?.decision === 'approved' && editorial.reviewerId && editorial.role &&
      validDate(editorial.reviewedAt) && editorial.sourceUrl === source?.url &&
      editorial.sourceVersionOrDate === sourceVersionOrDate &&
      clinical.reviewerId !== editorial.reviewerId
    );
  }

  function drawRound(questions, category, random = Math.random, sources = null) {
    const pool = questions.filter((question) => question.category === category && hasReleaseApproval(question, sources));
    if (pool.length < ROUND_LENGTH) throw new Error(`Category ${category} needs at least ${ROUND_LENGTH} approved questions.`);
    return shuffle(pool, random).slice(0, ROUND_LENGTH).map((question) => ({
      ...question,
      choices: shuffle(question.choices, random),
    }));
  }

  function validateQuestions(questions, sources) {
    if (!Array.isArray(questions)) throw new Error('Question bank must be an array.');
    const ids = new Set();
    const errors = [];
    for (const question of questions) {
      if (!question.id || ids.has(question.id)) errors.push(`Duplicate or missing question ID: ${question.id}`);
      ids.add(question.id);
      if (!question.category || !question.prompt || !question.explanation) errors.push(`Missing content: ${question.id}`);
      if (!Array.isArray(question.choices) || question.choices.length !== 4 || new Set(question.choices).size !== 4) errors.push(`Expected four distinct choices: ${question.id}`);
      if (!question.choices?.includes(question.answer)) errors.push(`Correct answer is absent: ${question.id}`);
      const source = sources?.[question.source];
      if (!source?.url || !source?.authority || !source?.checked) errors.push(`Missing reviewed source: ${question.id}`);
      if (question.reviewStatus === 'approved' && !hasReleaseApproval(question, sources)) errors.push(`Missing valid clinical and independent editorial release approval: ${question.id}`);
    }
    return errors;
  }

  return { QUESTION_SECONDS, ROUND_LENGTH, shownSeconds, pointsForAnswer, shuffle, hasReleaseApproval, drawRound, validateQuestions };
});
